import { ORDER_INQUIRY_KIND_LABELS, ORDER_INQUIRY_STATUS_LABELS } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { useOrderInquiryDetailQuery } from '@/entities/order-inquiry';
import { AnswerOrderInquiryForm } from '@/features/order-inquiry/answer';
import { formatDateTime } from '@/shared/lib';
import { EmptyState, StatusBadge } from '@/shared/ui';
import { OrderInquiryAnswerView } from './order-inquiry-answer-view';

interface OrderInquiryDetailProps {
  readonly inquiryId: string;
}

/**
 * 어드민 문의 상세 — 내용·사진·고객 정보·주문 링크, 처리 중이면 답변 폼, 답변 완료면 답변 뷰.
 * 열 때마다 서버가 개인정보 조회 기록을 남기므로 훅이 다시 부르지 않게 되어 있다.
 */
export const OrderInquiryDetail = ({ inquiryId }: OrderInquiryDetailProps): ReactElement => {
  const query = useOrderInquiryDetailQuery(inquiryId);

  if (query.isPending) return <EmptyState>불러오는 중…</EmptyState>;
  if (query.isError) {
    return <EmptyState isError>문의를 불러오지 못했습니다: {query.error.message}</EmptyState>;
  }
  const inquiry = query.data;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <StatusBadge tone={inquiry.status === 'OPEN' ? 'warning' : 'positive'}>
          {ORDER_INQUIRY_STATUS_LABELS[inquiry.status]}
        </StatusBadge>
        <span className="text-sm text-muted-foreground">
          {ORDER_INQUIRY_KIND_LABELS[inquiry.kind]} · 주문 {inquiry.orderNumber}
        </span>
      </div>

      <section className="rounded-2xl border border-foreground/[0.06] bg-card px-5 py-4">
        <p className="whitespace-pre-wrap text-sm">{inquiry.content}</p>
        {inquiry.images.length > 0 ? (
          <ul className="mt-4 flex flex-wrap gap-2">
            {inquiry.images.map((image) => (
              <li key={image.id}>
                {image.url === null ? (
                  <div className="flex size-28 items-center justify-center rounded-lg bg-foreground/[0.06] p-2 text-center text-xs text-muted-foreground">
                    보관 기간이 지난 사진
                  </div>
                ) : (
                  <img
                    src={image.url}
                    alt="문의 사진"
                    width={image.widthPx}
                    height={image.heightPx}
                    className="size-28 rounded-lg object-cover"
                  />
                )}
              </li>
            ))}
          </ul>
        ) : null}
        <p className="mt-4 text-xs text-muted-foreground">
          접수 {formatDateTime(inquiry.createdAt)}
        </p>
      </section>

      <dl className="grid grid-cols-[6rem_1fr] gap-y-2 rounded-2xl border border-foreground/[0.06] bg-card px-5 py-4 text-sm">
        <dt className="text-muted-foreground">고객</dt>
        <dd className="flex items-center gap-2">
          {inquiry.customer.name}
          {inquiry.customer.withdrawn ? (
            <StatusBadge tone="neutral">탈퇴한 회원</StatusBadge>
          ) : null}
        </dd>
        <dt className="text-muted-foreground">이메일</dt>
        <dd>{inquiry.customer.withdrawn ? '—' : inquiry.customer.email}</dd>
        <dt className="text-muted-foreground">주문 번호</dt>
        <dd>
          <Link
            to={`/orders/${inquiry.orderId}`}
            className="underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground"
          >
            {inquiry.orderNumber}
          </Link>
        </dd>
      </dl>

      {inquiry.status === 'OPEN' ? (
        <AnswerOrderInquiryForm inquiry={inquiry} />
      ) : (
        <OrderInquiryAnswerView inquiry={inquiry} />
      )}
    </div>
  );
};
